import type { ActionDescriptor } from '@finance/contracts';
import type { NavigateFunction } from 'react-router-dom';
import type { ResolvedAction } from './action-resolution';

export interface ActionExecutionContext {
  source: 'form' | 'table-row' | 'table-bulk' | 'table-create' | 'table-row-activation' | 'page' | 'workflow';
  confirmationHandled?: boolean;
  confirmationValues?: Record<string, unknown>;
  tableId?: string;
  rowId?: string;
  rowData?: Record<string, unknown>;
  selectedIds?: string[];
  savedViewId?: string | null;
  queryFingerprint?: string;
}

export interface ActionExecutionResult {
  executed: boolean;
  navigated: boolean;
  navigationTarget?: string | number;
}

export interface ExecuteResolvedActionOptions {
  resolved: ResolvedAction;
  onAction?: (action: ResolvedAction, context?: ActionExecutionContext) => void | Promise<void>;
  navigate: NavigateFunction;
  context?: ActionExecutionContext;
}

/**
 * One execution invariant for every descriptor-driven action surface.
 * Renderers may additionally present locked actions for step-up UX, but a
 * hidden, disabled, unavailable, or locked descriptor must never dispatch its
 * business action through a programmatic path.
 */
export function isResolvedActionExecutable(
  resolved: ResolvedAction | undefined,
): resolved is ResolvedAction {
  if (!resolved || !resolved.visible || resolved.disabled || !resolved.available) return false;
  const availability = resolved.action.availability ?? 'AVAILABLE';
  if (availability !== 'AVAILABLE' && availability !== 'READ_ONLY') return false;
  if (availability === 'READ_ONLY' && resolved.action.kind !== 'navigate') return false;
  return true;
}

export async function executeResolvedAction({
  resolved,
  onAction,
  navigate,
  context,
}: ExecuteResolvedActionOptions): Promise<ActionExecutionResult> {
  if (!isResolvedActionExecutable(resolved)) {
    return { executed: false, navigated: false };
  }

  const descriptor = resolved.action;
  const onActionResult = onAction?.(resolved, context);
  if (onActionResult && typeof onActionResult.then === 'function') {
    await onActionResult;
  }
  // A form submit owns validation, persistence, reset, toast, and success
  // navigation as one transaction. Re-applying the descriptor's success
  // behavior here would navigate before/after that transaction and can unmount
  // an in-flight request after the server has already committed it.
  const formOwnsSuccessBehavior = context?.source === 'form' && resolved.isFormSubmit;
  const navigationTarget = formOwnsSuccessBehavior
    ? undefined
    : applySuccessBehavior(descriptor, navigate, context?.rowData);
  return {
    executed: true,
    navigated: navigationTarget !== undefined,
    navigationTarget,
  };
}

function applySuccessBehavior(
  descriptor: ActionDescriptor,
  navigate: NavigateFunction,
  rowData?: Record<string, unknown>,
): string | number | undefined {
  const resolvedTarget = resolveNavigationTarget(descriptor.successNavigateTo, rowData);

  if (descriptor.kind === 'navigate') {
    if (resolvedTarget) {
      navigate(resolvedTarget);
      return resolvedTarget;
    }
    if (descriptor.successBehavior === 'NAVIGATE_BACK') {
      navigate(-1);
      return -1;
    }
    return undefined;
  }

  if (descriptor.successBehavior === 'NAVIGATE_TO' && resolvedTarget) {
    navigate(resolvedTarget);
    return resolvedTarget;
  }
  if (descriptor.successBehavior === 'NAVIGATE_BACK') {
    navigate(-1);
    return -1;
  }
  return undefined;
}

function resolveNavigationTarget(
  target: ActionDescriptor['successNavigateTo'],
  rowData?: Record<string, unknown>,
): string | undefined {
  if (!target) return undefined;
  const raw = String(target);
  if (!rowData) return raw;

  return raw
    .replace(/\{([^}]+)\}/g, (match, key: string) => {
      const value = rowData[key];
      return value == null ? match : encodeURIComponent(String(value));
    })
    .replace(/:([A-Za-z0-9_]+)/g, (match, key: string) => {
      const value = rowData[key];
      return value == null ? match : encodeURIComponent(String(value));
    });
}
